import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { collection, query, getDocs, orderBy, limit } from 'firebase/firestore';
import { firestore } from '../../database/firebase';
import {
  HiShieldCheck,
  HiKey,
  HiArrowPath,
  HiCheckCircle,
  HiArrowPathRoundedSquare,
  HiBell,
  HiMagnifyingGlass,
} from 'react-icons/hi2';

const SEVERITY_CONFIG = {
  critical: { bg: 'bg-red-900/50', text: 'text-red-300', dot: 'bg-red-500' },
  high: { bg: 'bg-orange-900/50', text: 'text-orange-300', dot: 'bg-orange-500' },
  medium: { bg: 'bg-amber-900/50', text: 'text-amber-300', dot: 'bg-amber-500' },
  low: { bg: 'bg-slate-700', text: 'text-slate-400', dot: 'bg-slate-500' },
  info: { bg: 'bg-blue-900/50', text: 'text-blue-300', dot: 'bg-blue-500' },
};

const toDate = v => {
  if (!v) return null;
  if (v?.toMillis) return new Date(v.toMillis());
  if (v instanceof Date) return v;
  return new Date(v);
};

const formatTime = d =>
  d ? d.toLocaleString(undefined, { dateStyle: 'short', timeStyle: 'medium' }) : '—';

const DevSecurityAuditPage = () => {
  const [activeTab, setActiveTab] = useState('security');
  const [securityLogs, setSecurityLogs] = useState([]);
  const [otpEvents, setOtpEvents] = useState([]);
  const [auditLogs, setAuditLogs] = useState([]);
  const [alerts, setAlerts] = useState([]);
  const [stats, setStats] = useState({ critical: 0, high: 0, total: 0 });
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [severityFilter, setSeverityFilter] = useState('all');

  const loadData = async () => {
    setLoading(true);
    try {
      const [secSnap, otpSnap, clientOtpSnap, resetOtpSnap, auditSnap, alertsSnap] =
        await Promise.all([
          (async () => {
            try {
              return await getDocs(
                query(
                  collection(firestore, 'security_logs'),
                  orderBy('timestamp', 'desc'),
                  limit(50)
                )
              );
            } catch {
              try {
                return await getDocs(
                  query(
                    collection(firestore, 'security_logs'),
                    orderBy('createdAt', 'desc'),
                    limit(50)
                  )
                );
              } catch {
                return { docs: [] };
              }
            }
          })(),
          getDocs(
            query(collection(firestore, 'otps'), orderBy('createdAt', 'desc'), limit(30))
          ).catch(() => ({ docs: [] })),
          getDocs(
            query(collection(firestore, 'clientLoginOtps'), orderBy('createdAt', 'desc'), limit(20))
          ).catch(() => ({ docs: [] })),
          getDocs(
            query(
              collection(firestore, 'passwordResetOtps'),
              orderBy('createdAt', 'desc'),
              limit(20)
            )
          ).catch(() => ({ docs: [] })),
          getDocs(
            query(collection(firestore, 'stageAuditLogs'), orderBy('affectedAt', 'desc'), limit(40))
          ).catch(() => ({ docs: [] })),
          getDocs(
            query(collection(firestore, 'alerts'), orderBy('timestamp', 'desc'), limit(25))
          ).catch(() => ({ docs: [] })),
        ]);

      const sec = (secSnap?.docs || []).map(d => {
        const data = d.data();
        const ts = data.timestamp ?? data.createdAt;
        return { id: d.id, source: 'security', ...data, createdAt: ts };
      });
      const otps = [
        ...(otpSnap?.docs || []).map(d => ({ id: d.id, source: 'otp', ...d.data() })),
        ...(clientOtpSnap?.docs || []).map(d => ({ id: d.id, source: 'clientOtp', ...d.data() })),
        ...(resetOtpSnap?.docs || []).map(d => ({ id: d.id, source: 'resetOtp', ...d.data() })),
      ].sort((a, b) => (toDate(b.createdAt) || 0) - (toDate(a.createdAt) || 0));
      const audits = (auditSnap?.docs || []).map(d => ({ id: d.id, ...d.data() }));
      const alertList = (alertsSnap?.docs || []).map(d => ({ id: d.id, ...d.data() }));

      setSecurityLogs(sec);
      setOtpEvents(otps.slice(0, 50));
      setAuditLogs(audits);
      setAlerts(alertList);

      setStats({
        critical: sec.filter(s => (s.severity || '').toLowerCase() === 'critical').length,
        high: sec.filter(s => (s.severity || '').toLowerCase() === 'high').length,
        total: sec.length,
      });
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('Security audit load failed:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  const filteredLogs = securityLogs.filter(log => {
    const matchSearch =
      !search ||
      (log.action || '').toLowerCase().includes(search.toLowerCase()) ||
      (log.details || '').toLowerCase().includes(search.toLowerCase()) ||
      (log.uid || '').toLowerCase().includes(search.toLowerCase());
    const matchSev =
      severityFilter === 'all' || (log.severity || '').toLowerCase() === severityFilter;
    return matchSearch && matchSev;
  });

  const tabs = [
    { id: 'security', label: 'Security Logs', icon: HiShieldCheck },
    { id: 'otp', label: 'OTP Events', icon: HiKey },
    { id: 'audit', label: 'Audit Trail', icon: HiArrowPathRoundedSquare },
    { id: 'alerts', label: 'Alerts', icon: HiBell },
  ];

  return (
    <div className="flex flex-col gap-6">
      <motion.header
        initial={{ opacity: 0, y: -10 }}
        animate={{ opacity: 1, y: 0 }}
        className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between"
      >
        <div className="flex items-center gap-4">
          <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-amber-600 text-white shadow-lg shadow-amber-500/25">
            <HiShieldCheck className="h-6 w-6" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-white">Security & Audit</h1>
            <p className="text-sm text-slate-400">
              Security logs, OTP events, audit trails, and system alerts
            </p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <div className="flex gap-2">
            <div className="rounded-lg border border-red-900/50 bg-red-950/30 px-3 py-1.5">
              <span className="text-xs font-semibold text-red-400">{stats.critical} Critical</span>
            </div>
            <div className="rounded-lg border border-orange-900/50 bg-orange-950/30 px-3 py-1.5">
              <span className="text-xs font-semibold text-orange-400">{stats.high} High</span>
            </div>
          </div>
          <motion.button
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            onClick={loadData}
            disabled={loading}
            className="flex items-center gap-2 rounded-lg border border-slate-600 bg-slate-800 px-4 py-2 text-sm font-medium text-slate-200 transition-all hover:border-amber-500/50 hover:bg-slate-700 disabled:opacity-60"
          >
            <HiArrowPath className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </motion.button>
        </div>
      </motion.header>

      {/* Tabs */}
      <div className="flex flex-wrap gap-2">
        {tabs.map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`flex items-center gap-2 rounded-lg border px-4 py-2.5 text-sm font-semibold transition-all ${
              activeTab === tab.id
                ? 'border-amber-500 bg-amber-600 text-white'
                : 'border-slate-600 bg-slate-800 text-slate-400 hover:border-slate-500 hover:bg-slate-700 hover:text-slate-200'
            }`}
          >
            <tab.icon className="h-4 w-4" />
            {tab.label}
          </button>
        ))}
      </div>

      <AnimatePresence mode="wait">
        {activeTab === 'security' && (
          <motion.section
            key="security"
            initial={{ opacity: 0, x: 10 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -10 }}
            className="space-y-4"
          >
            <div className="flex flex-wrap gap-3">
              <div className="relative min-w-[200px] flex-1">
                <HiMagnifyingGlass className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-500" />
                <input
                  type="text"
                  value={search}
                  onChange={e => setSearch(e.target.value)}
                  placeholder="Search logs..."
                  className="w-full rounded-lg border border-slate-600 bg-slate-800 py-2 pl-10 pr-4 text-sm text-white placeholder-slate-500 focus:border-amber-500 focus:outline-none focus:ring-1 focus:ring-amber-500"
                />
              </div>
              <div className="flex gap-1">
                {['all', 'critical', 'high', 'medium', 'low'].map(sev => (
                  <button
                    key={sev}
                    onClick={() => setSeverityFilter(sev)}
                    className={`rounded-lg px-3 py-1.5 text-xs font-medium capitalize ${
                      severityFilter === sev
                        ? 'bg-amber-600 text-white'
                        : 'bg-slate-800 text-slate-400 hover:bg-slate-700'
                    }`}
                  >
                    {sev}
                  </button>
                ))}
              </div>
            </div>
            <div className="overflow-hidden rounded-xl border border-slate-700/50 bg-slate-800/50">
              <div className="max-h-[480px] overflow-y-auto">
                <table className="w-full">
                  <thead className="sticky top-0 bg-slate-800/95 backdrop-blur">
                    <tr className="border-b border-slate-700">
                      <th className="px-4 py-3 text-left text-xs font-semibold uppercase text-slate-400">
                        Severity
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-semibold uppercase text-slate-400">
                        Action
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-semibold uppercase text-slate-400">
                        User
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-semibold uppercase text-slate-400">
                        Time
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-700/50">
                    {loading ? (
                      <tr>
                        <td colSpan={4} className="px-4 py-12 text-center text-sm text-slate-500">
                          Loading security logs...
                        </td>
                      </tr>
                    ) : filteredLogs.length === 0 ? (
                      <tr>
                        <td colSpan={4} className="px-4 py-12 text-center text-sm text-slate-500">
                          No security logs match your filters.
                        </td>
                      </tr>
                    ) : (
                      filteredLogs.map(log => {
                        const sev = (log.severity || 'low').toLowerCase();
                        const cfg = SEVERITY_CONFIG[sev] || SEVERITY_CONFIG.low;
                        return (
                          <tr key={log.id} className="hover:bg-slate-700/30">
                            <td className="px-4 py-3">
                              <span
                                className={`inline-flex items-center gap-1.5 rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase ${cfg.bg} ${cfg.text}`}
                              >
                                <span className={`h-1.5 w-1.5 rounded-full ${cfg.dot}`} />
                                {sev}
                              </span>
                            </td>
                            <td className="px-4 py-3 text-sm text-slate-200">
                              {log.action || '—'}
                            </td>
                            <td className="px-4 py-3 font-mono text-xs text-slate-400">
                              {log.uid?.slice(0, 12) || '—'}
                            </td>
                            <td className="px-4 py-3 text-xs text-slate-500">
                              {formatTime(toDate(log.createdAt ?? log.timestamp))}
                            </td>
                          </tr>
                        );
                      })
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </motion.section>
        )}

        {activeTab === 'otp' && (
          <motion.section
            key="otp"
            initial={{ opacity: 0, x: 10 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -10 }}
            className="overflow-hidden rounded-xl border border-slate-700/50 bg-slate-800/50"
          >
            <div className="max-h-[480px] overflow-y-auto">
              <table className="w-full">
                <thead className="sticky top-0 bg-slate-800/95 backdrop-blur">
                  <tr className="border-b border-slate-700">
                    <th className="px-4 py-3 text-left text-xs font-semibold uppercase text-slate-400">
                      Type
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-semibold uppercase text-slate-400">
                      Email
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-semibold uppercase text-slate-400">
                      Status
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-semibold uppercase text-slate-400">
                      Time
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-700/50">
                  {loading ? (
                    <tr>
                      <td colSpan={4} className="px-4 py-12 text-center text-sm text-slate-500">
                        Loading OTP events...
                      </td>
                    </tr>
                  ) : otpEvents.length === 0 ? (
                    <tr>
                      <td colSpan={4} className="px-4 py-12 text-center text-sm text-slate-500">
                        No OTP events.
                      </td>
                    </tr>
                  ) : (
                    otpEvents.map((ev, i) => (
                      <tr key={ev.id || i} className="hover:bg-slate-700/30">
                        <td className="px-4 py-3">
                          <span className="rounded-full bg-slate-700 px-2 py-0.5 text-[10px] font-medium text-slate-300">
                            {ev.source === 'clientOtp'
                              ? 'Client Login'
                              : ev.source === 'resetOtp'
                                ? 'Password Reset'
                                : 'OTP'}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-sm text-slate-200">{ev.email || '—'}</td>
                        <td className="px-4 py-3">
                          <span className={ev.consumed ? 'text-green-400' : 'text-amber-400'}>
                            {ev.consumed ? 'Verified' : 'Pending'}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-xs text-slate-500">
                          {formatTime(toDate(ev.createdAt))}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </motion.section>
        )}

        {activeTab === 'audit' && (
          <motion.section
            key="audit"
            initial={{ opacity: 0, x: 10 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -10 }}
            className="overflow-hidden rounded-xl border border-slate-700/50 bg-slate-800/50"
          >
            <div className="max-h-[480px] overflow-y-auto">
              <table className="w-full">
                <thead className="sticky top-0 bg-slate-800/95 backdrop-blur">
                  <tr className="border-b border-slate-700">
                    <th className="px-4 py-3 text-left text-xs font-semibold uppercase text-slate-400">
                      User
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-semibold uppercase text-slate-400">
                      Change
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-semibold uppercase text-slate-400">
                      Collection
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-semibold uppercase text-slate-400">
                      Time
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-700/50">
                  {loading ? (
                    <tr>
                      <td colSpan={4} className="px-4 py-12 text-center text-sm text-slate-500">
                        Loading audit trail...
                      </td>
                    </tr>
                  ) : auditLogs.length === 0 ? (
                    <tr>
                      <td colSpan={4} className="px-4 py-12 text-center text-sm text-slate-500">
                        No audit logs.
                      </td>
                    </tr>
                  ) : (
                    auditLogs.map(log => (
                      <tr key={log.id} className="hover:bg-slate-700/30">
                        <td className="px-4 py-3 text-sm text-slate-200">
                          {log.userEmail || log.userId || '—'}
                        </td>
                        <td className="px-4 py-3">
                          <span className="font-mono text-xs text-amber-300">
                            {log.previousStage} → {log.newStage}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-xs text-slate-400">
                          {log.collection || '—'}
                        </td>
                        <td className="px-4 py-3 text-xs text-slate-500">
                          {formatTime(toDate(log.affectedAt))}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </motion.section>
        )}

        {activeTab === 'alerts' && (
          <motion.section
            key="alerts"
            initial={{ opacity: 0, x: 10 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -10 }}
            className="space-y-3"
          >
            {loading ? (
              <div className="rounded-xl border border-slate-700/50 bg-slate-800/50 p-12 text-center text-sm text-slate-500">
                Loading alerts...
              </div>
            ) : alerts.length === 0 ? (
              <div className="flex flex-col items-center justify-center rounded-xl border-2 border-dashed border-slate-700 py-16 text-center">
                <HiCheckCircle className="h-12 w-12 text-green-500/50" />
                <p className="mt-3 text-sm font-medium text-slate-400">No alerts</p>
                <p className="mt-1 text-xs text-slate-500">System is healthy</p>
              </div>
            ) : (
              alerts.map(alert => {
                const type = (alert.type || alert.severity || 'info').toLowerCase();
                const cfg = SEVERITY_CONFIG[type] || SEVERITY_CONFIG.info;
                return (
                  <div
                    key={alert.id}
                    className={`rounded-lg border px-4 py-3 ${cfg.bg} border-slate-700/50`}
                  >
                    <div className="flex items-start justify-between gap-4">
                      <div>
                        <p className="font-medium text-slate-100">
                          {alert.title || alert.message || 'Alert'}
                        </p>
                        {alert.message && alert.title && (
                          <p className="mt-1 text-sm text-slate-400">{alert.message}</p>
                        )}
                        <p className="mt-1 text-xs text-slate-500">
                          {formatTime(toDate(alert.timestamp))}
                        </p>
                      </div>
                      <span
                        className={`shrink-0 rounded px-2 py-0.5 text-[10px] font-semibold uppercase ${cfg.text}`}
                      >
                        {type}
                      </span>
                    </div>
                  </div>
                );
              })
            )}
          </motion.section>
        )}
      </AnimatePresence>
    </div>
  );
};

export default DevSecurityAuditPage;
